import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  startTransition,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import FamilyIcon from "../../../../../assets/images/searchbars/family.png";
import "../../HotelSearchBar/hotelsearchbar.scss";
import RoomsCounter from "../../HotelSearchBar/RoomsCounter/roomscounter";
import { useOnClickOutside } from "../../../../../utils/ComponentsUtils";
import { parsePeopleCount } from "../../../../../contexts/SearchContext";
import {
  cloneSearchRoomInfo,
  searchRoomInfoEquals,
} from "../../../../../utils/searchOccupancyDraft";

const PeopleCounterInput = ({
  roomInfo,
  setRoomInfo,
  isTour = false,
  /** shared usage בין flight / hotel / package */
  productType = "hotel",
  /**
   * מיני־סייט חבילות: רינדור ה־RoomsCounter בתוך שדה הטריגר (absolute) כדי שלא יינעל ל־viewport
   * ויופיע ליד מנוע החיפוש. במובייל נשאר portal ל־body (מגירה מלאה).
   */
  mountDropdownInTrigger = false,
}) => {
  const resolvedProductType = productType === "flight" ? "flight" : "package";
  const isFlightProduct = resolvedProductType === "flight";

  const [open, setOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [draftRoomInfo, setDraftRoomInfo] = useState(null);

  const ref = useRef(null);
  const roomsCounterRef = useRef(null);
  const [position, setPosition] = useState(null);
  const draftRef = useRef(null);
  const committedRoomInfoRef = useRef(roomInfo);
  const setRoomInfoRef = useRef(setRoomInfo);

  const { t } = useTranslation();
  const touchHandledRef = useRef(false);

  draftRef.current = draftRoomInfo;
  committedRoomInfoRef.current = roomInfo;
  setRoomInfoRef.current = setRoomInfo;

  const occupancy = draftRoomInfo || roomInfo;

  useEffect(() => {
    if (!isFlightProduct) return;
    if (!Array.isArray(roomInfo?.rooms) || roomInfo.rooms.length <= 1) return;

    const mergedRoom = roomInfo.rooms.reduce(
      (acc, room) => {
        const kidsAges = Array.isArray(room?.kidsAges) ? room.kidsAges : [];
        return {
          adults: acc.adults + (room?.adults || 0),
          kids: acc.kids + (room?.kids || 0),
          infants: acc.infants + (room?.infants || 0),
          kidsAges: [...acc.kidsAges, ...kidsAges],
        };
      },
      { adults: 0, kids: 0, infants: 0, kidsAges: [] }
    );

    setRoomInfo({
      rooms: [
        {
          adults: Math.max(1, mergedRoom.adults),
          kids: mergedRoom.kids,
          infants: mergedRoom.infants,
          kidsAges: mergedRoom.kidsAges.slice(0, mergedRoom.kids),
        },
      ],
    });
  }, [isFlightProduct, roomInfo, setRoomInfo]);

  const closePicker = () => {
    const draft = draftRef.current;
    setOpen(false);
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      document.body.classList.remove("rooms-counter-open");
    }
    if (!draft) return;
    if (searchRoomInfoEquals(draft, committedRoomInfoRef.current)) {
      setDraftRoomInfo(null);
      return;
    }
    startTransition(() => {
      setRoomInfoRef.current(draft);
      setDraftRoomInfo(null);
    });
  };

  const openPicker = () => {
    setDraftRoomInfo(cloneSearchRoomInfo(roomInfo));
    setOpen(true);
    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      document.body.classList.add("rooms-counter-open");
    }
  };

  const toggleOpen = (e) => {
    if (e && e.cancelable) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (open) {
      closePicker();
      return;
    }
    openPicker();
  };

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    if (mountDropdownInTrigger) return;
    if (typeof window === "undefined" || window.innerWidth <= 768) {
      setPosition(null);
      return;
    }
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    });
  }, [open, mountDropdownInTrigger]);

  const handleTouchEnd = (e) => {
    touchHandledRef.current = true;
    if (e.cancelable) e.preventDefault();
    toggleOpen(e);
    setTimeout(() => { touchHandledRef.current = false; }, 400);
  };

  const handleClick = (e) => {
    if (touchHandledRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    toggleOpen(e);
  };

  useOnClickOutside(ref, (event) => {
    // Don't close if clicking inside rooms counter
    if (roomsCounterRef.current && roomsCounterRef.current.contains(event.target)) {
      return;
    }
    if (!datePickerOpen && open) {
      closePicker();
    }
  });
  

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && window.innerWidth <= 768) {
        document.body.classList.remove("rooms-counter-open");
      }
    };
  }, []);

  const peopleCount = parsePeopleCount(occupancy);

  const isMobileVp =
    typeof window !== "undefined" && window.innerWidth <= 768;
  const canMountDropdownInTrigger =
    mountDropdownInTrigger && !isMobileVp && ref.current != null;
  const portalContainer =
    open && typeof document !== "undefined"
      ? canMountDropdownInTrigger
        ? ref.current
        : document.body
      : null;

  return (
    <div 
      className="hotelSearchBar-item people-counter-field" 
      ref={ref} 
      style={{
        position: "relative",
        zIndex: 10000011,
        pointerEvents: "auto",
        cursor: "pointer",
        overflow: mountDropdownInTrigger ? "visible" : undefined,
      }}
    >
      <div
        className="hotelSearchBar-desc-wrapper cursor-pointer"
        onClick={handleClick}
        onTouchEnd={handleTouchEnd}
        style={{
          cursor: 'pointer',
          pointerEvents: 'auto',
          position: 'relative',
          zIndex: 10000012,
          WebkitTapHighlightColor: 'transparent',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          touchAction: 'manipulation'
        }}
      >
        <div className="hotelSearchBar-icon-wrapper">
          <img
            className="hotelSearchBar-icon2 cursor-pointer"
            src={FamilyIcon}
            alt=""
          ></img>
        </div>

        <div className="hotelSearchBar-label-wrapper cursor-pointer">
          <label className="hotelSearchBar-label1 cursor-pointer">
            {isFlightProduct
              ? t("flightsearch.passengerCountLabel")
              : t("hotelsearchbar.rooms")}
          </label>

          <label
            className="hotelSearchBar-label2 cursor-pointer"
            style={{ display: "flex", flexDirection: "row", gap: 6, alignItems: "center" }}
          >
            {isFlightProduct ? (
              <span style={{ whiteSpace: "nowrap" }}>
                {peopleCount.totalPeople} {t("roomscounter.passengers")}
              </span>
            ) : (
              <>
                <span style={{ whiteSpace: "nowrap" }}>
                  {peopleCount.totalRooms} {t("hotelsearchbar.roomsnumber")}
                </span>
                <span style={{ whiteSpace: "nowrap" }}>
                  • {peopleCount.totalPeople} {t("roomscounter.passengers")}
                </span>
              </>
            )}
          </label>
        </div>
      </div>

      {portalContainer &&
        createPortal(
          <RoomsCounter
            ref={roomsCounterRef}
            setDatePickerOpen={setDatePickerOpen}
            isOpen={open}
            onClose={closePicker}
            position={position}
            anchoredToParent={Boolean(open && canMountDropdownInTrigger)}
            premiumPanel={mountDropdownInTrigger}
            productType={resolvedProductType}
            roomInfo={occupancy}
            setRoomInfo={setDraftRoomInfo}
          />,
          portalContainer
        )}
    </div>
  );
};

export default React.memo(PeopleCounterInput);
